import { Label } from '../ui/label'
import { Input } from '../ui/input'
import { useFormContext } from 'react-hook-form'
import { ComponentProps, useMemo, useState } from 'react'
import { cn } from '../../../app/utils/cn'
import { Eye, EyeOff } from 'lucide-react'
import { dateMask } from '@/app/utils/dateFormater'

export interface BasicInputProps extends ComponentProps<'input'> {
  label?: string | React.ReactNode
  error?: string
  className?: string
  name: string
  labelClass?: string
  variant?: 'default' | 'percentage'
  subLabel?: string
  sizeInput?: 'normal' | 'large'
  isPassword?: boolean
}

export const BasicInput = ({
  error,
  label,
  name,
  className,
  labelClass,
  variant = 'default',
  subLabel,
  sizeInput = 'normal',
  isPassword = false,
  ...props
}: BasicInputProps) => {
  const { register } = useFormContext()
  const [showPassword, setShowPassword] = useState(false)

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const inputType = useMemo(() => {
    if (isPassword && !showPassword) return 'password'

    if (props.type === 'date') return 'text'

    return props.type || 'text'
  }, [isPassword, props.type, showPassword])

  return (
    <Label
      htmlFor={name}
      className={cn('w-full text-sm font-medium', labelClass)}
    >
      {label}
      <div
        className={cn(
          'relative flex flex-col',
          'mt-2 h-[38px] bg-transparent',
          props.disabled && 'bg-white02 rounded-xl dark:bg-gray-800',
          subLabel && 'mb-4',
          sizeInput === 'large' && 'h-fit',
          className,
        )}
      >
        <Input
          className={cn(sizeInput === 'large' && 'h-14 py-4 text-lg')}
          {...register(`${name}`)}
          id={props.id || name}
          {...props}
          type={inputType}
          onInput={
            props.onInput || (props?.type === 'date' ? dateMask : undefined)
          }
        />
        {variant === 'percentage' && (
          <span className="absolute right-1 top-2 flex items-center bg-white02 px-1 font-semibold">
            %
          </span>
        )}
        {isPassword && (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute right-3 top-1/2 -translate-y-1/2 transform rounded p-1 hover:bg-gray-100"
            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4 text-gray-500" />
            ) : (
              <Eye className="h-4 w-4 text-gray-500" />
            )}
          </button>
        )}
        {subLabel && <p className="text-[11px] text-black04">{subLabel}</p>}
      </div>

      {error && (
        <div className="mt-1 min-h-5 font-light text-error">{error}</div>
      )}
    </Label>
  )
}
